import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { ROLE_HOME, loginSchema, type LoginInput, type SystemRole } from '@aurea/contracts';
import { ApiRequestError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { AuthScene } from './AuthScene';

/** Единая точка входа для сотрудников и соискателей — страница aurea.you. */
export function LoginPage() {
  const { user, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  if (isLoading) return null;
  if (user) return <Navigate to={homeFor(user.role.code)} replace />;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const loggedIn = await login(values);
      const from = (location.state as { from?: string } | null)?.from;
      void navigate(from ?? homeFor(loggedIn.role.code), { replace: true });
    } catch (error) {
      setFormError(
        error instanceof ApiRequestError
          ? error.message
          : 'Не получилось войти. Проверьте соединение.',
      );
    }
  });

  return (
    <AuthScene>
      <h1 className="auth__title">Вход</h1>

      <form onSubmit={onSubmit} noValidate>
        {formError && (
          <div className="auth__note" role="alert">
            {formError}
          </div>
        )}

        <label className="auth__field">
          <span className="auth__label">Почта</span>
          <input
            className="auth__input"
            type="email"
            autoComplete="username"
            placeholder="you@example.com"
            {...register('email')}
          />
          {errors.email?.message && <span className="auth__error">{errors.email.message}</span>}
        </label>

        <label className="auth__field">
          <span className="auth__label">Пароль</span>
          <input
            className="auth__input"
            type="password"
            autoComplete="current-password"
            {...register('password')}
          />
          {errors.password?.message && (
            <span className="auth__error">{errors.password.message}</span>
          )}
        </label>

        <button className="auth__submit" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Входим…' : 'Войти'}
        </button>
      </form>
    </AuthScene>
  );
}

function homeFor(roleCode: string): string {
  return ROLE_HOME[roleCode as SystemRole] ?? '/';
}
